import { CircleAlert } from "lucide-react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./process-button.i18n";

type ProcessButtonProps = {
  instanceId: string;
  clientId: string;
  /** Outstanding, undismissed feedback. Hidden at zero. */
  messageCount: number;
  /** A current stop — adds the attention icon on top of the red. */
  stopped: boolean;
};

/**
 * The only entry to process details and history, and always the rightmost
 * action in the header.
 *
 * Red means "there is something here": unread messages OR a stopped process.
 * The attention icon is narrower and marks a stop alone, so dismissing every
 * message clears the red of an otherwise healthy flow but never hides an
 * unresolved stop.
 *
 * The counter is the undismissed stack, shared for the flow — opening Process
 * deliberately does not clear it, because reading is not dismissing.
 */
export function ProcessButton({ instanceId, clientId, messageCount, stopped }: ProcessButtonProps) {
  const navigate = useNavigate();
  // QL-33 — keep standalone params when moving to the process page.
  const { withStandaloneParams } = useStandaloneMode();
  const t = useT(T);

  const needsAttention = messageCount > 0 || stopped;

  return (
    <Button
      variant={needsAttention ? "destructive" : "outline"}
      onClick={() =>
        navigate(withStandaloneParams(`/clients/${clientId}/flows/${instanceId}/process`))
      }
      className="gap-2"
    >
      {stopped && <CircleAlert className="h-4 w-4" />}
      {t.process}
      {messageCount > 0 && (
        <span
          role="status"
          aria-label={t.unreadMessages(messageCount)}
          className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-background px-1.5 text-xs font-semibold text-foreground"
        >
          {messageCount > 99 ? "99+" : messageCount}
        </span>
      )}
    </Button>
  );
}
